import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'src/axios';
import { AppContext } from 'src/store/ContextStore';
import { APICONSTANTS } from 'src/components/Constants';
import { translate } from 'src/hooks/useTranslate';
import {
  DISMISS_REFUSED_AFTER_MATCH,
  SINGLE_FETUS_DISMISS_REFUSED,
  UNDETERMINED_ROLE_TEXT
} from 'src/components/lab-report-inbox/labReportInboxVocabulary';

// Structured test results from lab reports, read side (TGCLOUD-1866, Phase 2 rework: a report is
// applied to its Test by saving the Test).
//
// THE FLAG. FEATURE_STRUCTURED_TEST_RESULTS, per group and two-tier (backend
// services/testResults/featureFlag.js), arrives with every other systemFeature from
// GET /settings/features. Only an explicit `true` is on: an absent flag (the fetch failed, an
// older server) is off, so nothing new appears and no /test-results request is made. The server
// refuses every route while it is off anyway; the client check is so that nothing is drawn, not
// the gate.
//
// THE READ. The lab report records of one Test (GET /test-results/by-test/:testNoteId). A 403
// with the flag's own code means the RECORD's group has it off (a System Admin looking into a group
// that has not switched it on): that is `off`, and the screens draw nothing, rather than an error.
// "Genes tested" per person (GET /test-results/genes/:personId) is not read: the person's GENOTYPES
// no longer lists it, since the Test record is where the genes a test covered live.
//
// A CHANGE, SAID ONCE. Applying or withdrawing a report changes what a Test's reader should show,
// from somewhere that has no line to it. notifyTestResultsChanged() tells every mounted reader to
// read again.

const FEATURE_OFF = 'STRUCTURED_TEST_RESULTS_OFF';

/** Is the structured test results feature on for the signed-in user's group? */
export function useStructuredTestResultsEnabled() {
  const { content } = useContext(AppContext) || {};
  return (
    content?.settings?.systemFeatures?.FEATURE_STRUCTURED_TEST_RESULTS === true
  );
}

const listeners = new Set();

/** Tell every mounted reader that a confirmed result was added or withdrawn. */
export function notifyTestResultsChanged(detail = {}) {
  for (const listener of [...listeners]) {
    try {
      listener(detail);
    } catch (err) {
      console.error('A test results listener failed:', err);
    }
  }
}

/** Call `callback` whenever notifyTestResultsChanged() is called, while mounted. */
export function useTestResultsChanged(callback) {
  const ref = useRef(callback);
  ref.current = callback;
  useEffect(() => {
    const listener = (detail) => ref.current?.(detail);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
}

const isFeatureOff = (err) =>
  err?.response?.status === 403 && err?.response?.data?.code === FEATURE_OFF;

// One GET, re-run when `url` changes or `reload()` is called.
//
// WHAT IS SHOWN IS ALWAYS THIS URL'S. Each answer is kept with the url it answers, and is returned
// only while that is still the url asked for: switching the sidebar to another person shows
// nothing until their own answer arrives, never the previous person's genes under the new
// person's name. An answer to an earlier request is dropped. A reload of the same url keeps the
// current answer on screen until the new one replaces it.
const EMPTY_READ = { url: null, data: null, error: false, off: false };

function useRead(url, pick) {
  const [answer, setAnswer] = useState(EMPTY_READ);
  const [pending, setPending] = useState(false);
  const [tick, setTick] = useState(0);
  const latest = useRef(0);
  const pickRef = useRef(pick);
  pickRef.current = pick;

  useEffect(() => {
    const id = (latest.current += 1);
    if (!url) {
      setAnswer(EMPTY_READ);
      setPending(false);
      return;
    }
    setPending(true);
    axios
      .get(url)
      .then((res) => {
        if (latest.current !== id) return;
        setAnswer({
          url,
          data: pickRef.current(res.data),
          error: false,
          off: false
        });
        setPending(false);
      })
      .catch((err) => {
        if (latest.current !== id) return;
        setAnswer({
          url,
          data: null,
          error: !isFeatureOff(err),
          off: isFeatureOff(err)
        });
        setPending(false);
      });
  }, [url, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  const current = url && answer.url === url ? answer : EMPTY_READ;
  return {
    data: current.data,
    error: current.error,
    off: current.off,
    loading: !!url && (pending || answer.url !== url),
    reload
  };
}

/**
 * The lab report records for one Test (GET /test-results/by-test/:testNoteId): one per report
 * applied to it, current, superseded or entered in error (a pending record, from a save that never
 * finished, is never returned). Each is `{ format, version, labReport, identifiers }`, where
 * `labReport` is the record (backend services/testResults/export.js).
 *
 * @param {string|null} testNoteId a SAVED Test's id; null reads nothing
 * @param {{ enabled: boolean }} options
 */
export function useLabReportsForTest(testNoteId, { enabled }) {
  const url =
    enabled && testNoteId
      ? APICONSTANTS.testResultsByTest.replace(
          ':testNoteId',
          encodeURIComponent(testNoteId)
        )
      : null;
  const read = useRead(url, (data) =>
    Array.isArray(data?.labReports) ? data.labReports : []
  );
  useTestResultsChanged(read.reload);
  return {
    labReports: read.data || [],
    loading: read.loading,
    error: read.error,
    off: read.off,
    reload: read.reload
  };
}

// WHY A SAVE THAT APPLIES A LAB REPORT WAS REFUSED, IN WORDS (TGCLOUD-1868 B2, B4; round 2,
// decisions 13, 14 and 16). The Test save answers 422 TEST_RECORD_INVALID with every reason, each a
// code the server also explains; the codes below are said as the verification view says them, and
// name the reading's groups and values in words where the view can (`context.nameGroup(key)`,
// `context.nameValue(path)`), never a code or a path. A code this list does not know keeps the
// server's own sentence.
const listOf = (names) => names.filter(Boolean).join('; ');
// A sentence of this file in the selected language (TGCLOUD-1893). Each wording below gives its
// English as a string, or as { text, vars } where a value goes into it; translated here, where it
// is said, never at import. A server's own sentence is never passed through this.
const inWords = (said) =>
  !said
    ? said
    : typeof said === 'string'
    ? translate(said)
    : translate(said.text, said.vars);
const named = (keys, name) =>
  typeof name === 'function' && Array.isArray(keys)
    ? listOf(keys.map((k) => name(k)))
    : '';
const GENES_SHORT = Object.freeze({
  one: Object.freeze({
    one: 'The report states {{stated}} gene analysed and {{listed}} is listed, so this is not every gene the laboratory analysed. Enter the rest, or choose “Only some of the genes are recorded”.',
    many: 'The report states {{stated}} gene analysed and {{listed}} are listed, so this is not every gene the laboratory analysed. Enter the rest, or choose “Only some of the genes are recorded”.'
  }),
  many: Object.freeze({
    one: 'The report states {{stated}} genes analysed and {{listed}} is listed, so this is not every gene the laboratory analysed. Enter the rest, or choose “Only some of the genes are recorded”.',
    many: 'The report states {{stated}} genes analysed and {{listed}} are listed, so this is not every gene the laboratory analysed. Enter the rest, or choose “Only some of the genes are recorded”.'
  })
});
const SAVE_REASON_TEXT = Object.freeze({
  VERIFICATION_INCOMPLETE: (r, { nameGroup } = {}) => {
    const left = named(r.groups, nameGroup);
    return left
      ? {
          text: 'Confirm every part of the report before saving. Not confirmed yet: {{left}}.',
          vars: { left }
        }
      : 'Confirm every part of the report before saving.';
  },
  VERIFICATION_UNKNOWN_GROUP: () =>
    'A confirmation names a part of the report that this reading does not have. Open the report again.',
  VERIFICATION_DUPLICATE_GROUP: () =>
    'A part of the report was confirmed more than once. Confirm each part once.',
  VERIFICATION_UNKNOWN_FINDING: () =>
    'A variant names this report but is not one of the findings read from it. Open the report again.',
  VERIFICATION_DUPLICATE_FINDING: () =>
    'A finding of the report is on the Test more than once. Keep it once.',
  LOW_CONFIDENCE_EMPTY: (r, { nameValue } = {}) => {
    const empty = named(r.paths, nameValue);
    return empty
      ? {
          text: 'A value that could not be read from the report with confidence is empty: {{empty}}. Type or choose each one as the report prints it.',
          vars: { empty }
        }
      : 'A value that could not be read from the report with confidence is empty. Type or choose each one as the report prints it.';
  },
  GENES_TESTED_LIST_REQUIRED: () =>
    'Say what the genes tested are: every gene the laboratory analysed, only some of them, or no gene list captured from this report.',
  GENE_LIST_MANUAL_REQUIRED: () =>
    'The report’s list of genes was not read with confidence, so the genes it analysed are entered by hand. Remove the gene rows taken from the reading, and add the genes with “Add genes”.',
  GENES_TESTED_LIST_COMPLETE_EMPTY: () =>
    'A complete gene list names at least one gene. List the genes, or choose “No gene list was captured from this report”.',
  GENES_TESTED_LIST_COMPLETE_SCOPE: () =>
    'An exome or a genome is recorded by its scope, so its gene list cannot be every gene the laboratory analysed. Choose “Only some of the genes are recorded” or “No gene list was captured from this report”.',
  // Decision 21: a report that states two different gene counts is never "complete".
  GENES_TESTED_LIST_COUNT_AMBIGUOUS: () =>
    'The report states more than one number of genes analysed, so this list cannot be every gene the laboratory analysed. Choose “Only some of the genes are recorded” or “No gene list was captured from this report”.',
  // The counts as the server sends them (testRecord.js: `statedCount` and `listed`).
  // A plural is a sentence of its own: one for each of the two counts' forms.
  GENES_TESTED_LIST_SHORT: (r) =>
    Number.isFinite(r.statedCount) && Number.isFinite(r.listed)
      ? {
          text: GENES_SHORT[r.statedCount === 1 ? 'one' : 'many'][
            r.listed === 1 ? 'one' : 'many'
          ],
          vars: { stated: r.statedCount, listed: r.listed }
        }
      : 'Fewer genes are listed than the report states, so this is not every gene the laboratory analysed. Enter the rest, or choose “Only some of the genes are recorded”.',
  GENE_ROW_NOT_FROM_REPORT: () =>
    'A gene row names a lab report that did not give it in this save. Remove the row and add the gene by hand, or apply the report again.',
  ROW_NAMES_UNLINKED_REPORT: () =>
    'A value names a lab report that is not applied to this Test. Apply the report again, or remove what came from it.',
  ROW_NAMES_WITHDRAWN_REPORT: () =>
    'A value names a lab report that has been marked as entered in error. Remove it, or open the Test again to see it as it is now.',
  GENOME_BUILD_REQUIRED: () =>
    'A variant from a lab report needs its genome build (GRCh37 or GRCh38). It is never inferred.',
  TRANSCRIPT_REQUIRED: () =>
    'A variant from a lab report needs the versioned transcript its c. description is numbered against.',
  HGVS_REQUIRED: () =>
    'A variant from a lab report needs its c. or g. description.'
});

// THE REFUSALS TGCLOUD-1868 ADDED, IN WORDS (round 5: "every refusal code and evidence detail gets its
// own wording"). The server lists every code it added in backend/services/testResults/rules.js
// (REFUSAL_CODES), with the `reason` beside a code that covers cases the screens word apart
// (REFUSAL_REASONS). Each is said here, in the view's words, wherever it is met (an upload on a Test,
// the inbox's hand-off, the save, a decision in the inbox, the verification figures), and never as the
// code or a field name. src/components/lab-report-inbox/labReportRefusalVocabulary.test.js binds this
// to that list: a code or a reason the server adds with no words here fails it. The codes TGCLOUD-1866
// shipped keep the server's own sentence.
//
// Where the view knows more, it says more:
//   INDIVIDUAL_NOT_FOR_THIS_TEST   which person of a report fits which Test (409,
//                                  labReportIntake.individualFit), by the server's `reason`:
//                                  INDIVIDUAL_FIT_TEXT. A refusal with no reason is said for the person
//                                  the view knows the reading is for (`context.individual`, `{ key,
//                                  role }`), else both ways round. `single-fetus-report` is a report
//                                  about ONE fetus (its samples show a fetus, its results are one
//                                  patient's), taken only onto a Test of that fetus: a Test not for a
//                                  fetus, or for another fetus record
//   INBOX_DISMISS_AFTER_MATCH,     on a single-fetus report's item (`context.singleFetus`, decision 32)
//   INBOX_ALL_SET_ASIDE            said for its one fetus
export const SINGLE_FETUS_REPORT = 'single-fetus-report';
// A fetus by its role, else by its key: `fetus`, or `fetus-<n>` for one of two or more (decision 23).
const isFetusIndividual = (individual) =>
  !!individual &&
  (individual.role === 'fetus' ||
    (!individual.role && /^fetus(?:-\d+)?$/.test(individual.key || '')));
const FETUS_RESULT_TEXT =
  'This is the fetus’s result on the report, and this Test is not that fetus’s. Read it into a Test added for the fetus from the pregnancy.';
const NOT_A_FETUS_RESULT_TEXT =
  'This person’s result on the report is not a fetus’s, and this Test is about a pregnancy. Read it into a Test of their own.';
// Each case of INDIVIDUAL_NOT_FOR_THIS_TEST, by the `reason` the server gives it
// (rules.REFUSAL_REASONS.INDIVIDUAL_NOT_FOR_THIS_TEST).
const INDIVIDUAL_FIT_TEXT = Object.freeze({
  'fetus-result': FETUS_RESULT_TEXT,
  'not-a-fetus-result': NOT_A_FETUS_RESULT_TEXT,
  // A twin or a baby the report does not say is a fetus or a born child (rules.UNDETERMINED_ROLES).
  'role-undetermined':
    'The report does not say whether this twin or baby is a fetus or a born child, so their result is not read into any Test. Enter it by hand, as the report gives it, on the Test it belongs to.',
  // The save: the reading was signed for another person of the report than this Test is about.
  'another-individual':
    'This reading is for another person of the report than this Test is about. Open the report again and choose who this Test is for.',
  // Decision 2 and the Phase E reconciliation: a fetus's result, on a Test for no fetus or another.
  [SINGLE_FETUS_REPORT]:
    'This report gives a fetus’s result, and this Test is not that fetus’s. Read it into a Test added for the fetus from its mother’s pregnancy.'
});
const REFUSAL_TEXT = Object.freeze({
  // ── The lab report inbox: one person of a report about several, or a single-fetus report's fetus ──
  INBOX_INDIVIDUAL_REQUIRED: () =>
    'This report gives results for more than one person, so each of them is matched, and read into a Test, on their own. Open the report in the Lab Report Inbox again.',
  INBOX_INDIVIDUAL_NOT_FOUND: () =>
    'That person is not one of the people this report gives results for. Open the report in the Lab Report Inbox again.',
  INBOX_NO_INDIVIDUALS: () =>
    'This report is about one person, so it is matched, or dismissed, as a whole. Open it in the Lab Report Inbox again.',
  INBOX_INDIVIDUALS_CHANGED: () =>
    'This report is now read as naming its people differently from when it arrived in the Lab Report Inbox, so a match made there cannot be relied on. Dismiss the report in the Lab Report Inbox and add it again, so that its people are read afresh and matched again.',
  INBOX_INDIVIDUAL_SET_ASIDE: () =>
    'This person on the report is set aside in the Lab Report Inbox as not this service’s patient. Undo that there before reading their result into a Test.',
  INBOX_ITEM_INDIVIDUALS: () =>
    'This report is waiting in the Lab Report Inbox as a report about more than one person. Match each of them there, and read their result into a Test of their own from the inbox.',
  // Decision 12's own words.
  INBOX_ITEM_WEAK_MATCH: () =>
    'This report is waiting in the Lab Report Inbox and its match to this person is weak: confirm it there first.',
  INBOX_DISMISS_AFTER_MATCH: (data, { singleFetus } = {}) =>
    singleFetus ? SINGLE_FETUS_DISMISS_REFUSED : DISMISS_REFUSED_AFTER_MATCH,
  INBOX_ALL_SET_ASIDE: (data, { singleFetus } = {}) =>
    singleFetus
      ? 'The fetus on this report is set aside, so the report is not reopened. Undo the set-aside, and the report comes back to the Lab Report Inbox with it.'
      : 'Everyone on this report is set aside, so it is not reopened whole. Undo the set-aside of the person to match, and the report comes back to the Lab Report Inbox with them.',
  INBOX_FROM_RUN: () =>
    'This report could not be added to the Lab Report Inbox as it was sent from the Test. Upload it to the Lab Report Inbox instead.',
  INDIVIDUAL_ROLE_UNDETERMINED: () => UNDETERMINED_ROLE_TEXT,
  FETUS_RECORD_NOT_FOUND: () =>
    'That fetus record is not in a pregnancy of the mother this report names. It may have changed in the pedigree since this page was read: open the report again.',
  MATCH_SAME_PERSON: () =>
    'This record is already matched to someone else on this report, and one record is never two people of one report. Undo that match first, or choose another record.',
  VERIFICATION_PARTICULARS_REQUIRED: () =>
    'A weak match is confirmed only with how it was verified and at least two particulars compared with the report, such as the name and the date of birth.',
  // ── One person's part of a report, read into a Test and saved ──
  INDIVIDUAL_KEY_INVALID: () =>
    'The person chosen is not one this report lists. Open the report again and choose who this Test is for.',
  PRENATAL_SCOPE_INVALID: () =>
    'The pregnancy and fetus this Test is for could not be read, so the report was not read for a fetus. Open the Test again from the pregnancy, then add the report again.',
  INDIVIDUAL_NOT_IN_REPORT: () =>
    'This report gives no result for the person chosen. Open the report again and choose who this Test is for.',
  INDIVIDUAL_NOT_FOR_THIS_TEST: (data, { individual } = {}) => {
    const reason = data && typeof data.reason === 'string' ? data.reason : '';
    if (Object.prototype.hasOwnProperty.call(INDIVIDUAL_FIT_TEXT, reason)) {
      return INDIVIDUAL_FIT_TEXT[reason];
    }
    if (!individual) {
      return 'This person’s result on the report does not fit this Test. A fetus’s result is read into a Test added for that fetus from the pregnancy, and anyone else’s into a Test that is not about a pregnancy.';
    }
    return isFetusIndividual(individual)
      ? FETUS_RESULT_TEXT
      : NOT_A_FETUS_RESULT_TEXT;
  },
  REPORT_PEOPLE_NOT_SPLIT: () =>
    'This report gives results for more than one person, or for a pregnancy, and the reader could not tell whose each result is, so nothing in it is read into this Test. Enter the result of the person this Test is for by hand, as the report gives it.',
  // Round 4: the family holds the report WHOLE, applied before each person's result on a report was
  // read on its own.
  REPORT_APPLIED_WHOLE: () =>
    'This report is already applied whole to another Test of this family, from before each person’s result on a report was read on its own. Mark the report entered in error on that Test first, then read this person’s result again.',
  // Migration 0052 not run (a customer-hosted install): a second person's record of one report.
  REPORT_INDIVIDUAL_INDEX_MISSING: () =>
    'This report gives results for more than one person, and this installation’s database has not been updated to keep a lab report record for each of them. Ask your administrator to run the database update, then save again. Nothing was saved.',
  REPORT_OTHER_PERSON_ON_TEST: () =>
    'This Test already holds another person’s result from this report, and a Test holds one person’s result. Read this person’s result into a Test of their own.',
  REPORT_OTHER_PERSON_SAME_PERSON: () =>
    'This record already holds another person’s result from this report, on another Test. One person, or one fetus record, is never two people of one report: check which result is theirs, and mark the other entered in error if it is not.',
  // Decision 20's own words.
  LAB_REPORT_SENT_TO_INBOX: () =>
    'This report was sent to the Lab Report Inbox from this Test. Match it there.',
  LAB_REPORT_TOKEN_STALE: () =>
    'This reading was made by an earlier version of TrakGene. Open the report again.',
  // ── How reports were checked: a review's record (never shown: best effort), and the figures ──
  VERIFICATION_SESSION_INVALID: () => 'This review could not be recorded.',
  VERIFICATION_SESSION_NOT_FOUND: () => 'This review is not recorded here.',
  VERIFICATION_SESSION_FINISHED: () =>
    'This review has already been recorded as finished.',
  VERIFICATION_REPORT_ACCESS: () =>
    'Only a System Admin, Genetics Service or Genetics Service Admin can see how lab reports were checked.',
  VERIFICATION_REPORT_INVALID: () =>
    'These figures cannot be made for what was chosen. Check the period and the grouping.',
  VERIFICATION_REPORT_TOO_LARGE: () =>
    'There are too many reviews in this period to show at once. Choose a shorter period.'
});

// The codes and reasons said in the view's own words, for labReportRefusalVocabulary.test.js to bind
// to the server's lists both ways (a code the view words that the server no longer lists fails too).
export const WORDED_REFUSAL_CODES = Object.freeze(Object.keys(REFUSAL_TEXT));
export const WORDED_INDIVIDUAL_FIT_REASONS = Object.freeze(
  Object.keys(INDIVIDUAL_FIT_TEXT)
);
export const WORDED_SAVE_REASON_CODES = Object.freeze(
  Object.keys(SAVE_REASON_TEXT)
);

/** A refusal as a whole, in the view's words, for a code it knows; else null. */
export function refusalText(data, context = {}) {
  const code = data && typeof data.code === 'string' ? data.code : null;
  const say =
    code && Object.prototype.hasOwnProperty.call(REFUSAL_TEXT, code)
      ? REFUSAL_TEXT[code]
      : null;
  return inWords(say && say(data, context || {})) || null;
}

/** One reason of a refusal, in words: the view's for a code it knows, else the server's. */
export function reasonText(reason, context = {}) {
  if (typeof reason === 'string') return reason;
  if (!reason || typeof reason !== 'object') return '';
  const say =
    typeof reason.code === 'string' &&
    Object.prototype.hasOwnProperty.call(SAVE_REASON_TEXT, reason.code)
      ? SAVE_REASON_TEXT[reason.code]
      : null;
  return (say && inWords(say(reason, context))) || reason.message || '';
}

/**
 * What a failed /test-results request says to the clinician. The server's own message where it
 * wrote one for a refusal (4xx: the route's messages name what was refused, never a value, see
 * backend/services/testResults/errors.js), with every reason a validation refusal gave, each in
 * words (reasonText); anything else is a generic line, so a stack or a provider's error never
 * reaches the screen.
 *
 * @param {*} err
 * @param {string} fallback
 * @param {{ nameGroup?: (key: string) => string, nameValue?: (path: string) => string,
 *   individual?: { key: string, role?: string|null } }} [context] names for the groups and values of
 *   the reading a save applied, and the person of the report a reading is for (REFUSAL_TEXT)
 */
export function describeTestResultError(err, fallback, context = {}) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  // The licence gate's refusal (a lapsed trial) carries no sentence of its own.
  if (status === 403 && data.groupAccess) {
    return {
      status,
      code: 'GROUP_ACCESS',
      message: translate(
        'This workspace is read-only, so a lab report cannot be added.'
      ),
      reasons: []
    };
  }
  const known =
    status && status >= 400 && status < 500 ? refusalText(data, context) : null;
  if (
    status &&
    status >= 400 &&
    status < 500 &&
    (known || typeof data.message === 'string')
  ) {
    return {
      status,
      code: data.code || null,
      message: known || data.message,
      // Each said once: a reason the server gives per gene row reads the same for every row.
      reasons: Array.isArray(data.reasons)
        ? data.reasons
            .map((r) => reasonText(r, context || {}))
            .filter((text, i, all) => !!text && all.indexOf(text) === i)
        : []
    };
  }
  return {
    status: status || null,
    code: null,
    // `fallback` is the caller's, already in the selected language.
    message: !status
      ? translate('{{fallback}} Check your connection and try again.', {
          fallback
        })
      : fallback,
    reasons: []
  };
}
